/**
 * CSRF visibility helpers for Classic Site diagnostics.
 *
 * document.cookie only exposes cookies for the current page origin.
 * Backend cookies (csrf_token on Django host) are not readable cross-origin
 * even when the browser sends them on credentialed fetch requests.
 */

import { getCsrfToken } from "../../../api/hoodieClient";
import { isCrossOriginDjango } from "./djangoOrigin";

export const CSRF_COOKIE_NAME = "csrf_token";

/** @returns {boolean} True only if csrf_token appears in document.cookie on this origin. */
export function isCsrfCookieReadableInJavaScript() {
  return Boolean(getCsrfToken());
}

/** @deprecated Use isCsrfCookieReadableInJavaScript — name implied backend cookie presence. */
export function isCsrfCookiePresent() {
  return isCsrfCookieReadableInJavaScript();
}

/**
 * @typedef {'readable'|'not_readable_cross_origin'|'not_on_this_origin'|'unknown'} CsrfJsVisibility
 */

/**
 * @typedef {Object} CsrfCookieDiagnostic
 * @property {CsrfJsVisibility} jsVisibility
 * @property {boolean} readableInJavaScript
 * @property {boolean} crossOrigin
 * @property {string} shortLabel
 * @property {string} detailNote
 * @property {boolean} mutationHeaderAvailable
 */

/**
 * Diagnostic labels for UI — never includes token values.
 * @param {{ isAuthenticated?: boolean }} [options]
 * @returns {CsrfCookieDiagnostic}
 */
export function getCsrfCookieDiagnostic(options = {}) {
  const { isAuthenticated = false } = options;
  const readableInJavaScript = isCsrfCookieReadableInJavaScript();
  const crossOrigin = isCrossOriginDjango();

  if (readableInJavaScript) {
    return {
      jsVisibility: "readable",
      readableInJavaScript: true,
      crossOrigin,
      shortLabel: "Readable in JavaScript (this origin)",
      detailNote:
        "csrf_token is visible to document.cookie on the React origin. hoodieFetch can set X-CSRFToken for mutations.",
      mutationHeaderAvailable: true,
    };
  }

  if (crossOrigin) {
    const authHint = isAuthenticated
      ? " With an authenticated session, DevTools may show csrf_token on credentialed requests to the Django host — that does not make it readable here."
      : "";
    return {
      jsVisibility: "not_readable_cross_origin",
      readableInJavaScript: false,
      crossOrigin: true,
      shortLabel: "Not readable in JavaScript (cross-origin)",
      detailNote:
        `The csrf_token cookie is scoped to the Django API host, not ${typeof window !== "undefined" ? window.location.origin : "the React origin"}. document.cookie cannot see it.${authHint} Mutations need a token value for X-CSRFToken (login HTML scrape, backend endpoint, or same-origin proxy).`,
      mutationHeaderAvailable: false,
    };
  }

  return {
    jsVisibility: "not_on_this_origin",
    readableInJavaScript: false,
    crossOrigin: false,
    shortLabel: "Not found on this origin",
    detailNote:
      "No csrf_token in document.cookie. Visit the Django site or complete a flow that sets the cookie on this origin before mutating.",
    mutationHeaderAvailable: false,
  };
}
